import { operationIds } from "@gen/app-access-config";
import { CoreQuery, type IClientResponse2 } from "@gen/app-query";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import type {
  GridColDef,
  GridPaginationModel,
  GridRowIdGetter,
  GridRowModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { startTransition, useCallback, useMemo } from "react";

import { DateTimeCell, ExpandCell } from "~/components/data_grid_cells";
import { ReadableDataGrid } from "~/components/readable_data_grid";
import { Select2, Select2TextPopover } from "~/components/select2";
import { useAuthOperations } from "~/contexts/auth_query";
import { useConfig } from "~/contexts/config";
import { useStableDebouncedCallback } from "~/utilities/timing";

import { ApiKeyCell } from "./ApiKey";

export function Clients() {
  const { canExecuteAdminOperation } = useAuthOperations();
  const { localization } = useConfig();
  const { page, pageSize, name, id, sortByName, sortByOrder } = useSearch({
    from: "/admin/clients",
  });
  const identity = useCallback(<T,>(x: T) => x, []);
  const clientsQueryOptions = useMemo(
    () => ({ placeholderData: identity }),
    [identity],
  );
  const { data, isFetching, refetch } = CoreQuery.useAdmin_getClientsQuery(
    page,
    pageSize,
    sortByName,
    sortByOrder,
    name,
    id,
    clientsQueryOptions,
  );
  const allColumns: GridColDef<IClientResponse2>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        minWidth: 216,
        sortable: false,
      },
      {
        field: "id",
        headerName: "Client ID",
        minWidth: 216,
        sortable: false,
      },
      {
        field: "api_key",
        headerName: "API Key",
        minWidth: 216,
        renderCell: (params) => <ApiKeyCell clientId={params.row.id ?? ""} />,
        sortable: false,
      },
      {
        field: "contact_email",
        headerName: "Contact Email",
        minWidth: 216,
        sortable: false,
      },
      {
        field: "base_url",
        headerName: "Base URL",
        minWidth: 216,
        flex: 1,
        renderCell: ExpandCell,
        sortable: false,
      },
      {
        field: "redirect_uris",
        headerName: "Redirect URI",
        minWidth: 216,
        sortable: false,
        renderCell: (params) => <ExpandCell {...params} value={params.value} />,
      },
      {
        field: "created",
        headerName: "Created",
        minWidth: 120,
        sortable: true,
        renderCell: (props) => (
          <DateTimeCell
            {...props}
            locale={localization.locale}
            options={{
              timeZone: localization.timeZone,
            }}
          />
        ),
      },
      {
        field: "updated",
        headerName: "Updated",
        minWidth: 120,
        sortable: false,
        renderCell: (props) => (
          <DateTimeCell
            {...props}
            locale={localization.locale}
            options={{
              timeZone: localization.timeZone,
            }}
          />
        ),
      },
    ],
    [localization.locale, localization.timeZone],
  );

  const navigate = useNavigate({ from: "/admin/clients" });
  const paginationModel: GridPaginationModel = useMemo(
    () => ({
      page: page - 1,
      pageSize,
    }),
    [page, pageSize],
  );

  const sortModel: GridSortModel = useMemo(() => {
    if (sortByName && sortByOrder) {
      return [
        {
          field: sortByName,
          sort: sortByOrder as "asc" | "desc",
        },
      ];
    }
    return [];
  }, [sortByName, sortByOrder]);

  const handleSortChange = useCallback(
    (model: GridSortModel) => {
      const next = model[0];
      navigate({
        search: (prev) => ({
          ...prev,
          sortByName: next?.field as "created" | undefined,
          sortByOrder: (next?.sort ?? undefined) as "asc" | "desc" | undefined,
          page: 1,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePaginationChange = useCallback(
    (model: GridPaginationModel) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: model.page + 1,
          pageSize: model.pageSize,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const updateFilter = useCallback(
    (field: "name" | "id", value: string | undefined) => {
      navigate({
        search: (prev) => ({
          ...prev,
          [field]: value,
          page: 1,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const deferApply = useStableDebouncedCallback((apply: () => void) => {
    startTransition(apply);
  }, 200);

  const handleClearAll = useCallback(() => {
    navigate({
      search: (prev) => ({
        ...prev,
        name: undefined,
        id: undefined,
        page: 1,
      }),
      replace: true,
    });
  }, [navigate]);

  const getClientUrl = (row: IClientResponse2) => `/admin/clients/${row.id}`;

  const activeFilterCount = (name ? 1 : 0) + (id ? 1 : 0);

  const columns = useMemo(() => {
    const hasApiKeyPermission = canExecuteAdminOperation(
      operationIds["core_admin.getClientApiKey"],
    );

    if (hasApiKeyPermission) {
      return allColumns;
    }

    const filteredColumns = allColumns.filter(
      (column) => column.field !== "api_key",
    );
    return filteredColumns;
  }, [allColumns, canExecuteAdminOperation]);

  const rows = data?.data ?? [];
  const rowCount = data?.records_filtered ?? 0;

  const getRowID: GridRowIdGetter = useCallback(
    (row: GridRowModel) => row.id,
    [],
  );

  return (
    <Stack spacing={2.5}>
      <Stack
        spacing={{ xs: 2, md: 3 }}
        sx={{
          backgroundColor: "primary._states.hover",
          borderRadius: 2,
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          flexWrap="wrap"
          useFlexGap
          sx={{ gap: 1 }}
        >
          <Select2
            label="Client Name"
            iconName="client_storefront"
            active={!!name}
            value={name}
            dataTestId="clients-name-filter-button"
            onClear={() => updateFilter("name", undefined)}
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={name ?? ""}
                onApply={(v) =>
                  updateFilter("name", v.length > 0 ? v : undefined)
                }
                onClose={close}
                placeholder="Filter Client Name"
                dataTestId="clients-name-filter-input"
                applyOnChange
                defer={deferApply}
              />
            )}
          />
          <Select2
            label="Client ID"
            iconName="client_id"
            active={!!id}
            value={id}
            dataTestId="clients-id-filter-button"
            onClear={() => updateFilter("id", undefined)}
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={id ?? ""}
                onApply={(v) =>
                  updateFilter("id", v.length > 0 ? v : undefined)
                }
                onClose={close}
                placeholder="Filter Client ID"
                dataTestId="clients-id-filter-input"
                applyOnChange
                defer={deferApply}
              />
            )}
          />
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleClearAll}
            disabled={activeFilterCount === 0}
          >
            Clear all
          </Button>
        </Stack>
      </Stack>
      <ReadableDataGrid
        dataTestId="clients-grid"
        onRefresh={() => {
          void refetch();
        }}
        columns={columns}
        rows={rows}
        getRowId={getRowID}
        rowCount={rowCount}
        loading={isFetching}
        showAllRows={false}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        sortingMode="server"
        sortModel={sortModel}
        sortingOrder={["asc", "desc", null]}
        onSortModelChange={handleSortChange}
        pageSizeOptions={[10, 25, 50]}
        rowNavigation={{
          getUrl: (row) => getClientUrl(row),
          onNavigate: (url) => navigate({ to: url }),
        }}
        localeText={{
          noRowsLabel: "No Clients",
        }}
      />
    </Stack>
  );
}
